"use client"
import type { DefinedInitialDataOptions } from "@tanstack/react-query"
import type { TDiscipline } from "@/models/discipline.model"
import type { TNavItems } from "@/models/navbar.model"
import type { Phase } from "@/models/phases.model"
import type { Unit } from "@/models/units.model"
import type { TCompetitionData } from "@/services/settings/competition"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useLocale, useTranslations } from "next-intl"
import { useParams, usePathname } from "next/navigation"
import React, { createElement, useMemo, useRef, useState } from "react"
import { Icon } from "@/components/ui/icon"
import { useRoutes } from "@/hooks/use-routes"
import { Link } from "@/i18n/navigation"
import { cn } from "@/lib/utils"
import { languageParser } from "@/mappers/language.mapper"
import { DisciplineQueryOptions, EventQueryOptions, PhaseQueryOptions, UnitQueryOptions } from "@/queries/disciplines.factory"
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator } from "./ui/breadcrumb"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu"
import { Input } from "./ui/input"
import { ScrollArea } from "./ui/scroll-area"

type apiItem = TDiscipline | TCompetitionData | Phase | Unit

function BreadcrumbDropdown({ item }: { item: { path: string, title: string, isLast: boolean } }) {
  const params = useParams()
  const routes = useRoutes()
  const { rscCode, unitCode } = params

  const standarizedCode = rscCode ? (rscCode as string).substring(0, 26).padEnd(34, "-") : unitCode ? (unitCode as string).substring(0, 26).padEnd(34, "-") : undefined
  const queryClient = useQueryClient()
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Queries
  const { data: disciplines } = useQuery(DisciplineQueryOptions.all)
  const { data: events } = useQuery(EventQueryOptions.byCode(params.disciplineCode as string))
  const { data: phases } = useQuery(PhaseQueryOptions.byCode(params.eventId as string))

  const phaseCodeForUnits = params.phasesId || standarizedCode
  const { data: units } = useQuery({
    ...UnitQueryOptions.byCode(phaseCodeForUnits as string),
    enabled: !!phaseCodeForUnits,
  })

  const eventCodeForUnits: string | undefined = useMemo(() => {
    if (unitCode) {
      return (unitCode as string).substring(0, 26).padEnd(34, "-")
    }
    else if (rscCode) {
      return (rscCode as string).substring(0, 26).padEnd(34, "-")
    }
    else {
      return undefined
    }
  }, [unitCode, rscCode])

  const { data: unitsBySportEvent } = useQuery({
    ...UnitQueryOptions.getBySportEvent(eventCodeForUnits as string),
    enabled: !!eventCodeForUnits,
  } as DefinedInitialDataOptions<Unit[] | undefined>)

  const [search, setSearch] = useState("")
  const t = useTranslations("generic")
  const pathname = usePathname()
  const locale = useLocale()

  // Find route with nested items that matches the current path
  const findRouteWithNestedItems = (path: string) => {
    for (const route of routes) {
      if (route.url === path && route.items) {
        return route
      }
      // Check nested items
      if (route.items) {
        for (const nestedItem of route.items) {
          if (nestedItem.url === path && nestedItem.subItems) {
            return { ...nestedItem, parentRoute: route }
          }
        }
      }
    }
    return null
  }

  const routeWithNestedItems = findRouteWithNestedItems(item.path)

  // Determine breadcrumb type
  const type = useMemo(() => {
    // Check if we have nested items from routes
    if (routeWithNestedItems) {
      if ("items" in routeWithNestedItems && routeWithNestedItems.items) {
        return "route-items"
      }
      if ("subItems" in routeWithNestedItems && routeWithNestedItems.subItems) {
        return "route-sub-items"
      }
    }

    // Para disciplinas
    if (params.disciplineCode && item.title.toLowerCase() === (params.disciplineCode as string)?.toLowerCase())
      return "discipline"

    // Para eventos
    if (params.eventId && item.title.toLowerCase() === (params.eventId as string)?.toLowerCase())
      return "event"

    // Para fases
    if (params.phasesId) {
      const phasesId = params.phasesId as string
      if (item.title.toLowerCase() === phasesId.toLowerCase())
        return "phase"

      if (item.title.toLowerCase().includes(phasesId.toLowerCase())
        || phasesId.toLowerCase().includes(item.title.toLowerCase())) {
        return "phase"
      }
    }

    // Para unidades
    if (params.rscCode || params.unitCode) {
      const unitCode = (params.rscCode || params.unitCode) as string
      if (item.title.toLowerCase() === unitCode.toLowerCase())
        return "unit"

      if (item.title.toLowerCase().includes(unitCode.toLowerCase())
        || unitCode.toLowerCase().includes(item.title.toLowerCase())) {
        return "unit"
      }
    }

    return undefined
  }, [item.title, params, routeWithNestedItems])

  // Mapping objects
  const PLACEHOLDERS = {
    "discipline": "disciplines",
    "event": "events",
    "phase": "phases",
    "unit": "units",
    "route-items": "items",
    "route-sub-items": "sub-items",
  } as const

  const COLORS = {
    "discipline": {
      background: "bg-green-100",
      text: "text-green-700",
      hover: "hover:bg-green-600 hover:text-white",
      focus: "focus:bg-green-600 focus:text-white",
    },
    "event": {
      background: "bg-sky-100",
      text: "text-sky-700",
      hover: "hover:bg-sky-600 hover:text-white",
      focus: "focus:bg-sky-600 focus:text-white",
    },
    "phase": {
      background: "bg-yellow-100",
      text: "text-yellow-700",
      hover: "hover:bg-yellow-500 hover:text-white",
      focus: "focus:bg-yellow-500 focus:text-white",
    },
    "unit": {
      background: "bg-purple-50",
      text: "text-purple-600",
      hover: "hover:bg-purple-600 hover:text-purple-50 hover:text-purple-50",
      focus: "focus:bg-purple-600 focus:text-purple-50",
      border: "border-purple-200",
    },
    "route-items": {
      background: "bg-background",
      text: "text-primary",
      hover: "hover:bg-background hover:text-primary",
      focus: "focus:bg-primary/20 focus:text-primary",
      border: "border-primary/20",
    },
    "route-sub-items": {
      background: "bg-background",
      text: "text-primary",
      hover: "hover:bg-background hover:text-primary",
      focus: "focus:bg-primary/20 focus:text-primary",
      border: "border-primary/20",
    },
    "default": {
      background: "bg-gray-100",
      text: "text-gray-700",
      hover: "hover:bg-gray-600 hover:text-white",
      focus: "focus:bg-gray-600 focus:text-white",
    },
  } as const

  const DATA = {
    "discipline": disciplines,
    "event": events,
    "phase": phases,
    "unit": units,
    "route-items": routeWithNestedItems && "items" in routeWithNestedItems ? routeWithNestedItems.items : undefined,
    "route-sub-items": routeWithNestedItems && "subItems" in routeWithNestedItems ? routeWithNestedItems.subItems : undefined,
  } as const

  const FILTER_FN = {
    "discipline": (d: TDiscipline) => d.description.long.toLowerCase().includes(search.toLowerCase()),
    "event": (e: TCompetitionData) => languageParser(e.description).long.toLowerCase().includes(search.toLowerCase()),
    "phase": (p: Phase) => languageParser(p.description).long.toLowerCase().includes(search.toLowerCase()),
    "unit": (u: Unit) => (u.description?.long || u.name || "").toLowerCase().includes(search.toLowerCase()),
    "route-items": (item: { title: string }) => item.title.toLowerCase().includes(search.toLowerCase()),
    "route-sub-items": (item: { title: string }) => item.title.toLowerCase().includes(search.toLowerCase()),
  } as const

  const filteredItems = type ? DATA[type]?.filter(FILTER_FN[type] as (value: apiItem | TNavItems) => boolean) : []
  const dropdownColor = COLORS[type ?? "default"]
  const placeholder = type ? PLACEHOLDERS[type] : ""

  const current = useMemo(() => {
    const basePath = `${pathname
      .split("/")
      .filter(segment => segment && segment !== locale)[0]}`
    if (type === "discipline") {
      return {
        code: "events" as const,
        path: `/${basePath}/`,
      }
    }
    else if (type === "event") {
      return {
        code: "phases" as const,
        path: `/${basePath}/${params.disciplineCode as string}/`,
      }
    }
    else if (type === "phase") {
      return {
        code: "units" as const,
        path: `/${basePath}/${params.disciplineCode as string}/${params.eventId as string}/`,
      }
    }
    else if (type === "unit") {
      // For units, we need to construct the path differently depending on the current route
      // If we're in a data-entry or simple-data-entry route, maintain that structure
      const pathSegments = pathname.split("/").filter(segment => segment && segment !== locale)
      const isDataEntry = pathSegments.includes("data-entry") || pathSegments.includes("simple-data-entry")
      
      if (isDataEntry) {
        // Build the path up to the unit code part for data-entry routes
        const dataEntryIndex = pathSegments.findIndex(segment => segment === "data-entry" || segment === "simple-data-entry")
        
        if (dataEntryIndex !== -1) {
          const basePathSegments = pathSegments.slice(0, dataEntryIndex + 1)
          const resultPath = `/${basePathSegments.join("/")}/`
          
          return {
            code: "units" as const,
            path: resultPath,
          }
        }
      }
      
      return undefined
    }
    else if (type === "route-items" || type === "route-sub-items") {
      return undefined
    }
    else {
      return undefined
    }
  }, [pathname, type, locale, params.disciplineCode, params.eventId])

  const prefetchHovered = (code: string, query: "events" | "phases" | "units" | undefined) => {
    if (!query)
      return
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(() => {
      if (query === "events") {
        void queryClient.prefetchQuery(EventQueryOptions.byCode(code))
      }
      else if (query === "phases") {
        void queryClient.prefetchQuery(PhaseQueryOptions.byCode(code))
      }
      else if (query === "units") {
        void queryClient.prefetchQuery(UnitQueryOptions.byCode(code))
      }
    }, 200)
  }

  const handleItemLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
  }

  // If no dropdown is needed (no nested items and no special type)
  if (placeholder === "" && !routeWithNestedItems) {
    return (
      <BreadcrumbLink asChild>
        <Link className="rounded-md px-2 py-1 text-sm text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground/80" href={item.path}>{item.title}</Link>
      </BreadcrumbLink>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger>
        <BreadcrumbLink asChild>
          <Link
            className={cn("flex max-w-[200px] items-center gap-1 rounded-md border border-primary-foreground/40 px-2 py-1 text-sm md:max-w-none", Object.values(dropdownColor))}
            href={item.path}
          >
            <span className="truncate">
              {(() => {
                // For route items, use the title directly
                if (type === "route-items" || type === "route-sub-items") {
                  return item.title
                }

                const exactMatch = filteredItems?.find((f: unknown) => item.title.toLowerCase() === (f as apiItem).code?.toLowerCase())
                if (exactMatch)
                  return (exactMatch as apiItem).name

                const partialMatch = filteredItems?.find((f: unknown) =>
                  (f as apiItem).code?.toLowerCase().includes(item.title.toLowerCase())
                  || item.title.toLowerCase().includes((f as apiItem).code?.toLowerCase() || ""),
                )

                if (partialMatch)
                  return (partialMatch as apiItem).name

                if (params.phasesId && phases) {
                  const phaseMatch = phases.find(p =>
                    p.code?.toLowerCase() === item.title.toLowerCase()
                    || p.code?.toLowerCase().includes(item.title.toLowerCase())
                    || item.title.toLowerCase().includes(p.code?.toLowerCase() || ""),
                  )
                  if (phaseMatch)
                    return phaseMatch.name
                }

                if ((params.rscCode || params.unitCode) && (units || unitsBySportEvent)) {
                  const unitsToCheck = units || unitsBySportEvent || []
                  const unitMatch = unitsToCheck.find(u =>
                    u.code?.toLowerCase() === item.title.toLowerCase()
                    || u.code?.toLowerCase().includes(item.title.toLowerCase())
                    || item.title.toLowerCase().includes(u.code?.toLowerCase() || ""),
                  )
                  if (unitMatch) {
                    return unitMatch?.description?.long || unitMatch?.name || unitMatch?.code
                  }
                }

                if (units || unitsBySportEvent) {
                  const unitsToCheck = units || unitsBySportEvent || []
                  const generalUnitMatch = unitsToCheck.find(u =>
                    u.code?.toLowerCase() === item.title.toLowerCase()
                    || u.code?.toLowerCase().includes(item.title.toLowerCase())
                    || item.title.toLowerCase().includes(u.code?.toLowerCase() || ""),
                  )
                  if (generalUnitMatch)
                    return generalUnitMatch.description?.long || generalUnitMatch.name || generalUnitMatch.code
                }

                return item.title
              })()}
            </span>
            <Icon className="h-4 w-4 flex-shrink-0" icon="lucide:chevron-down" />
          </Link>
        </BreadcrumbLink>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className={cn("w-80 p-4")}>
        <div className="space-y-4">
          <div className="relative">
            <Icon
              className="absolute top-2.5 left-2 h-4 w-4 text-muted-foreground"
              icon="lucide:search"
            />
            <Input
              className="pl-8"
              onChange={e => setSearch(e.target.value)}
              placeholder={`${t("search")} ${placeholder}`}
              value={search}
            />
          </div>
          <ScrollArea className="h-[300px] pr-4">
            <div className="space-y-1">
              {filteredItems?.map((item: unknown) => (
                <DropdownMenuItem key={(item as TNavItems).key || (item as apiItem).code} asChild>
                  <Link
                    className={cn("flex items-center", dropdownColor.text, dropdownColor.focus)}
                    href={(item as TNavItems).url || (current ? current.path + ((item as apiItem).code || (item as TNavItems).key) : "#")}
                    onMouseEnter={() => current && ((item as apiItem).code || (item as TNavItems).key) && prefetchHovered((item as apiItem).code || (item as TNavItems).key, current.code)}
                    onMouseLeave={handleItemLeave}
                  >
                    {(item as TNavItems).icon ? createElement((item as TNavItems).icon, { className: cn("mr-2 h-4 w-4", dropdownColor.text, dropdownColor.focus) }) : <Icon className={cn("mr-2 h-4 w-4", dropdownColor.text, dropdownColor.focus)} icon="lucide:dumbbell" />}
                    {(item as TNavItems).title || (item as apiItem).name}
                  </Link>
                </DropdownMenuItem>
              ))}
            </div>
          </ScrollArea>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function AutoBreadcrumb() {
  const pathname = usePathname()
  const t = useTranslations("navbar")
  const locale = useLocale()
  const routes = useRoutes()
  // Split the pathname into segments, remove empty strings, and filter out the locale
  const segments = pathname.split("/").filter(Boolean).filter(segment => segment !== locale)

  // Helper function to find a route by path, including nested items
  const findRouteByPath = (path: string) => {
    // First try to find in main routes
    const mainRoute = routes.find(r => r.url === path)
    if (mainRoute)
      return mainRoute

    // If not found, search in nested items
    for (const route of routes) {
      if (route.items) {
        const nestedRoute = route.items.find(item => item.url === path)
        if (nestedRoute)
          return nestedRoute
      }
    }
    return null
  }

  // Build the breadcrumb items
  const breadcrumbItems = segments.map((segment, index) => {
    // Build the path up to this segment
    const path = `/${segments.slice(0, index + 1).join("/")}`

    // Find the route that matches this path, including nested items
    const route = findRouteByPath(path)

    // If no route is found, use the segment as the title
    const title = route?.title || segment

    return {
      path,
      title,
      isLast: index === segments.length - 1,
    }
  })

  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link className="rounded-md px-2 py-1 text-sm text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground/80" href="/">{t("home")}</Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        {breadcrumbItems.map(item => (
          <React.Fragment key={item.path}>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbDropdown item={item} />
            </BreadcrumbItem>
          </React.Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  )
}
